import React from "react";

import { ToolTip, StadiumShippedTooltip, Multimedia } from "@constants/products/client";

import { FilterType } from "@stadium/shared/src/components/filter/types";

import sustainableImg from "@stadium/ui/src/assets/images/sustainable.svg";
import ecofriendlyImg from "@stadium/ui/src/assets/images/eco-friendly.svg";
import coreOnDemandImg from "@stadium/ui/src/assets/images/core-on-demand.svg";

import { isMobileDevice } from "@stadium/helpers/device";

import styles from "./main.module.scss";

export const INITIAL_PAGE = 1;
export const PRODUCT_LISTING_PER_PAGE = 96;
export const PRODUCT_LISTING_PER_PAGE_SWAG_CREATION = 48;
export const PRODUCT_PRICE_SLIDER_STEP = 10; //in USD
export const RELEVANCE_SORT_ITEM = { label: "Relevant", name: "Relevant", id: "relevance" };
export const RECOMMENDED_SORT_ITEM = {
  label: "Recommended",
  name: "Recommended",
  id: "recommended",
};

export const NEWLY_ADDED_SORT_ITEM = {
  label: "Newly Added",
  name: "Newly Added",
  id: "created_at:desc",
};

export const SORT_ITEMS = [
  NEWLY_ADDED_SORT_ITEM,
  { label: "Low to High", name: "Price - Low to High", id: "price:asc", priceDependent: true },
  { label: "High to Low", name: "Price - High to Low", id: "price:desc", priceDependent: true },
];

export const CORE_ON_DEMAND_TEXT =
  "Our core collection is available for all paid Packages. Additional items available on request.";

export const DEFAULT_QUANTITY = { box: 0 };

export const SIZE_FILTER_KEY = "size";
export const COLOR_FILTER_KEY = "color";

export const OPTION_VALUES_KEY = "option_values";
export const FILTERED_COLOR_VALUES_KEY = "filtered_color_values";
export const COLOR_VALUES_KEY = "color_values";
export const FILTERED_SIZE_VALUES_KEY = "filtered_size_values";
export const SIZE_VALUES_KEY = "size_values";

export const BRAND_FILTER_KEY = "brand";
export const COLLECTION_FILTER_KEY = "collection";
export const DIETARY_RESTRICTIONS_FILTER_KEY = "diet";
export const COUNTRY_FILTER_KEY = "country";
export const ITEM_FEATURES_FILTER_KEY = "itemFeatures";
export const PRODUCTION_DAYS_FILTER_KEY = "productionDays";
export const ITEM_TYPE_FILTER_KEY = "item-type";
export const DECORATION_METHOD_KEY = "decorationMethod";
export const CUSTOMIZABLE_FILTER_KEY = "customizable";
export const PRICE_FILTER_KEY = "price";
export const LOCKER_INVENTORY_FILTER_KEY = "locker-inventory";
export const DESIGNED_TYPE_FILTER_KEY = "designed-type";

export const SORTED_FILTER_ITEMS = [
  COLLECTION_FILTER_KEY,
  COUNTRY_FILTER_KEY,
  DESIGNED_TYPE_FILTER_KEY,
  DIETARY_RESTRICTIONS_FILTER_KEY,
  ITEM_TYPE_FILTER_KEY,
  PRICE_FILTER_KEY,
  ITEM_FEATURES_FILTER_KEY,
  BRAND_FILTER_KEY,
  DECORATION_METHOD_KEY,
  PRODUCTION_DAYS_FILTER_KEY,
  CUSTOMIZABLE_FILTER_KEY,
  COLOR_FILTER_KEY,
  SIZE_FILTER_KEY,
];

const DEFAULT_OBJ = {};

const generateNameForOnDemand = ({ label, tooltip }) => (
  <div className={styles.demandFilter}>
    <span>{label}</span>
    {isMobileDevice ? (
      <span className={styles.mobileTooltip}>(ships locally)</span>
    ) : (
      <div className={styles.iconInfoContainer}>
        <ToolTip
          containerClass={styles.toolTipWrapper}
          content={<div className={styles.toolTipText}>{tooltip}</div>}
          isLightTheme
          placement="top-start"
          arrow
        >
          <span className={styles.iconInfo} />
        </ToolTip>
      </div>
    )}
  </div>
);

export const ON_DEMAND_FILTER_TYPE = {
  id: "on-demand",
  getName: (label) =>
    generateNameForOnDemand({
      label,
      tooltip: "On-demand items are printed as you order, requiring no minimums.",
    }),
  label: "On-Demand",
};

export const ALL_ON_DEMAND_FILTER_TYPE = {
  ...ON_DEMAND_FILTER_TYPE,
  getName: (label) =>
    generateNameForOnDemand({
      label,
      tooltip: "On-demand items are printed as you order, requiring no minimums.",
    }),
  label: "All On-Demand",
};

export const CORE_ON_DEMAND_FILTER_TYPE = {
  id: "core-demand",
  name: (
    <div className={styles.demandFilter}>
      <span>Core On-Demand </span>
      {!isMobileDevice && (
        <div className={styles.iconInfoContainer}>
          <ToolTip
            className={styles.demandTooltip}
            content={<div className={styles.toolTipText}>{CORE_ON_DEMAND_TEXT}</div>}
            isLightTheme
            placement="top-start"
            arrow
          >
            <Multimedia
              type="image"
              src={coreOnDemandImg}
              height={16}
              isNextImage={false}
              className={styles.qualityImg}
            />
          </ToolTip>
        </div>
      )}
    </div>
  ),
  label: (
    <div className={styles.demandFilter}>
      <span>Core On-Demand</span>
      <div className={styles.iconInfoContainer}>
        <ToolTip
          containerClass={styles.toolTipWrapper}
          heading="Core On-Demand"
          content={CORE_ON_DEMAND_TEXT}
          isLightTheme
          placement="top-start"
          arrow
        >
          <Multimedia
            type="image"
            src={coreOnDemandImg}
            height={16}
            isNextImage={false}
            className={styles.qualityImg}
          />
        </ToolTip>
      </div>
    </div>
  ),
};
export const ADDITIONAL_ON_DEMAND_FILTER_TYPE = {
  id: "additional-on-demand",
  name: "Additional On-Demand",
  label: "Additional On-Demand",
  parentId: ON_DEMAND_FILTER_TYPE.id,
};

export const LOCKER_INVENTORY_FILTER_TYPE = {
  id: LOCKER_INVENTORY_FILTER_KEY,
  name: "Swag Inventory",
  label: "Swag Inventory",
};

export const BULK_ITEM_FILTER_TYPE = {
  id: "bulk-items",
  name: "Buy in Bulk",
  label: "Buy in Bulk",
};

export const ALL_PRODUCTS_FILTER = {
  id: "all-products",
  name: "All Products",
  label: "All Products",
};

export const VOLUME_DISCOUNT_FILTER = {
  id: "volume-discount",
  name: "Volume Discount",
  label: "Volume Discount",
};

export const ALL_DESIGNED_PRODUCTS_FILTER = {
  id: "all-designed-products",
  name: "All Designed Products",
  label: "All Designed Products",
};

export const MY_DESIGNED_PRODUCTS_FILTER = {
  id: "my-designed-products",
  name: "My Designed Products",
  label: "My Designed Products",
};

export const DESIGNED_BY_OTHERS_FILTER = {
  id: "designed-by-others",
  name: "Designed By Others",
  label: "Designed By Others",
};

export const FILTER_ITEM_TYPES = [
  BULK_ITEM_FILTER_TYPE,
  LOCKER_INVENTORY_FILTER_TYPE,
  ON_DEMAND_FILTER_TYPE,
  ALL_ON_DEMAND_FILTER_TYPE,
  CORE_ON_DEMAND_FILTER_TYPE,
  VOLUME_DISCOUNT_FILTER,
  ADDITIONAL_ON_DEMAND_FILTER_TYPE,
];

const BUNDLE_SHOP_ITEM_TYPES = [
  ALL_PRODUCTS_FILTER,
  ON_DEMAND_FILTER_TYPE,
  LOCKER_INVENTORY_FILTER_TYPE,
];

export const NEW_WORKSPACE_SHARING_DESIGNED_FILTERS = [
  MY_DESIGNED_PRODUCTS_FILTER,
  DESIGNED_BY_OTHERS_FILTER,
];

export const WORKSPACE_SHARING_DESIGNED_FILTERS = [
  ALL_DESIGNED_PRODUCTS_FILTER,
  ...NEW_WORKSPACE_SHARING_DESIGNED_FILTERS,
];

export const CUSTOMIZABLE_FILTER = {
  id: "custom",
  name: "Customizable",
};
export const DESIGNED_FILTER = { id: "designed", name: "My Designed Swag" };

const getCoreOnDemandFilter = () => ({
  ...CORE_ON_DEMAND_FILTER_TYPE,
  parentId: ON_DEMAND_FILTER_TYPE.id,
});

const getOnDemandFilterTypes = ({
  onDemandProductsCount,
  coreOnDemandProductsCount,
  isSwagCreationShopFlow = false,
}) => {
  const ON_DEMAND_FILTER = coreOnDemandProductsCount
    ? ALL_ON_DEMAND_FILTER_TYPE
    : ON_DEMAND_FILTER_TYPE;
  const onDemandItemTypes = [];
  const additionalOnDemandProductsCount = onDemandProductsCount - coreOnDemandProductsCount;

  //if no on-demand products available not need to display on-demand types
  if (!onDemandProductsCount || isSwagCreationShopFlow) {
    return [];
  }

  // if additional onDemand Products are present on-demand will be present either as a parent or as an individual filter.
  if (additionalOnDemandProductsCount) {
    onDemandItemTypes.push(ON_DEMAND_FILTER);
  }

  //this includes core-on-demand filter if both core-on-demand products are present.
  if (coreOnDemandProductsCount) {
    onDemandItemTypes.push(getCoreOnDemandFilter());
  }

  //this includes additional-on-demand filter if both core-on-demand products are present.
  if (additionalOnDemandProductsCount && coreOnDemandProductsCount) {
    onDemandItemTypes.push(ADDITIONAL_ON_DEMAND_FILTER_TYPE);
  }
  return onDemandItemTypes;
};

const getOnDemandFilterTypesV2 = ({ onDemandProductsCount, coreOnDemandProductsCount }) => {
  const additionalOnDemandProductsCount = onDemandProductsCount - coreOnDemandProductsCount;

  if (!onDemandProductsCount) {
    return [];
  }

  // If both core and additional products exist, nest them under an "All On-Demand" accordion header
  // Note: We use the dynamic suffix here for the group header
  if (coreOnDemandProductsCount && additionalOnDemandProductsCount) {
    return [
      {
        ...ALL_ON_DEMAND_FILTER_TYPE,
        children: [
          { ...getCoreOnDemandFilter(), id: CORE_ON_DEMAND_FILTER_TYPE.id },
          { ...ADDITIONAL_ON_DEMAND_FILTER_TYPE, id: ADDITIONAL_ON_DEMAND_FILTER_TYPE.id },
        ],
        childType: FilterType.MULTI_SELECT,
        childId: ITEM_TYPE_FILTER_KEY,
      },
    ];
  }

  // If only one type exists, show it directly without nesting
  if (coreOnDemandProductsCount) {
    return [getCoreOnDemandFilter()];
  }

  return [ON_DEMAND_FILTER_TYPE];
};

export const CUSTOM_FILTERS = {
  customizable: (asList = false) => {
    if (asList) {
      return [DESIGNED_FILTER];
    }
    return {
      label: "Customizable",
      labelDesigned: "Designed",
      items: [CUSTOMIZABLE_FILTER, DESIGNED_FILTER],
      key: CUSTOMIZABLE_FILTER_KEY,
    };
  },
  itemType: ({
    isBundleShop,
    bulkProductsCount,
    onDemandProductsCount,
    volumeDiscountProductsCount,
    coreOnDemandProductsCount,
    isSwagCreationShopFlow,
  }) => {
    const items = isBundleShop
      ? BUNDLE_SHOP_ITEM_TYPES
      : [
          ...getOnDemandFilterTypes({
            onDemandProductsCount,
            coreOnDemandProductsCount,
            isSwagCreationShopFlow,
          }),
          ...(bulkProductsCount ? [BULK_ITEM_FILTER_TYPE] : []),
          ...(volumeDiscountProductsCount ? [VOLUME_DISCOUNT_FILTER] : []),
        ];
    const showAllProducts = !isBundleShop && items.length > 1;
    return {
      label: "Item Type",
      defaultOpen: !isSwagCreationShopFlow,
      items: [...(showAllProducts ? [ALL_PRODUCTS_FILTER] : []), ...items],
      key: ITEM_TYPE_FILTER_KEY,
      type: items.length > 1 ? "radioButton" : "checkbox",
    };
  },
  decorationMethod: (printTypes) => {
    return {
      label: "Decoration Method",
      items: printTypes?.map(({ key }) => {
        return {
          id: key,
          label: key,
          name: key,
        };
      }),
      key: DECORATION_METHOD_KEY,
      type: "radioButton",
    };
  },
  workspaceSharing: {
    key: DESIGNED_TYPE_FILTER_KEY,
    label: "Workspace Sharing",
    defaultOpen: true,
    items: WORKSPACE_SHARING_DESIGNED_FILTERS,
    type: "radioButton",
  },
};

export const NEW_CUSTOM_FILTERS = {
  itemType: (opts) => {
    const { isBundleShop, bulkProductsCount, onDemandProductsCount, volumeDiscountProductsCount } =
      opts;

    return {
      label: "Order Quantity",
      defaultOpen: true,
      items: isBundleShop
        ? [
            ALL_PRODUCTS_FILTER,
            ...getOnDemandFilterTypesV2({ ...opts }),
            LOCKER_INVENTORY_FILTER_TYPE,
          ]
        : [
            ...(onDemandProductsCount && bulkProductsCount ? [ALL_PRODUCTS_FILTER] : []),
            ...getOnDemandFilterTypesV2({ ...opts }),
            ...(bulkProductsCount ? [BULK_ITEM_FILTER_TYPE] : []),
            ...(volumeDiscountProductsCount ? [VOLUME_DISCOUNT_FILTER] : []),
          ],
      key: ITEM_TYPE_FILTER_KEY,
      type: "radioButton",
    };
  },
};

export const ECO_FRIENDLY_FILTER = {
  id: "eco-friendly",
  name: (
    <>
      {" "}
      Eco-friendly &nbsp;
      <ToolTip
        containerClass={styles.toolTipWrapper}
        heading="Eco-friendly"
        content="This item is crafted from sustainable and recycled materials, reducing carbon footprint."
        isLightTheme
        placement="top-start"
        arrow={true}
        disableOnResponsive
      >
        <div className={styles.toolTipWrapCont}>
          <Multimedia
            type="image"
            src={ecofriendlyImg}
            height={16}
            isNextImage={false}
            className={styles.qualityImg}
          />
        </div>
      </ToolTip>
    </>
  ),
};

export const SUSTAINABLE_FILTER = {
  id: "sustainable",
  name: (
    <>
      {" "}
      Sustainable &nbsp;
      <ToolTip
        containerClass={styles.toolTipWrapper}
        heading="Sustainable Printing"
        content="We only print once ordered, which reduces waste and preserves resources."
        isLightTheme
        placement="top-start"
        arrow={true}
        disableOnResponsive
      >
        <div className={styles.toolTipWrapCont}>
          <Multimedia
            type="image"
            src={sustainableImg}
            height={16}
            isNextImage={false}
            className={styles.qualityImg}
          />
        </div>
      </ToolTip>
    </>
  ),
};

export const SHIPPED_FROM_STADIUM_FILTER = {
  id: "shipped-from-stadium",
  name: (
    <>
      Ships From Stadium &nbsp; <StadiumShippedTooltip />
    </>
  ),
};

export const SLUG_PRODUCT_IDENTIFIER_KEY = "variantsIncludingMaster";
export const SEARCH_PRODUCT_IDENTIFIER_KEY = "variants";

export const PRICE_LABEL_TEXT = "Price";

/** Filter key for context-aware category (menu) filter; when set, product fetch uses this for menuItemId and taxonIds */
export const MENU_CATEGORY_FILTER_KEY = "menu_category";

// Filters keys that need to be persisted in local storage
export const PERSISTED_FILTERS_KEY = ["itemType"];

export const PERSISTED_FILTER_STORAGE_KEY = "persisted-filters";
export const ONDEMAND_MODAL_SHOWN_KEY = "ondemand-modal-shown";

export const USD_PRICE_RANGE_KEY = "usd_range_price";
export const USD_PRICE_STATS_KEY = "usd_price_stats";
